import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { QrCode, Smartphone, Power, RefreshCw, Loader2 } from "lucide-react";

const MODES = [
  { key: "off", label: "Desligado", desc: "Nenhuma conexão com WhatsApp." },
  { key: "qr", label: "QR Code (provisório)", desc: "Conecta um número escaneando o QR, como no WhatsApp Web. Ideal para testes." },
  { key: "api", label: "API oficial (Meta)", desc: "WhatsApp Business API com token. Requer WHATSAPP_API_TOKEN e WHATSAPP_PHONE_ID no .env." },
];

const STATE_LABEL = { offline: "Conector parado", disconnected: "Desconectado", connecting: "Conectando...", qr: "Aguardando leitura do QR", connected: "Conectado" };

export default function WhatsAppSettings() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/integrations/whatsapp/status").then(({ data }) => setSt(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!st || st.mode !== "qr" || st.bridge?.state === "connected") return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [st, load]);

  const setMode = async (mode) => {
    setBusy(true);
    try { await api.put("/integrations/whatsapp/mode", { mode }); toast.success("Modo atualizado"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro ao alterar modo"); }
    finally { setBusy(false); }
  };
  const act = async (path, okMsg) => {
    setBusy(true);
    try { await api.post(`/integrations/whatsapp/qr/${path}`); toast.success(okMsg); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setBusy(false); }
  };

  if (!st) return <div className="text-sm text-slate-500">Carregando...</div>;
  const b = st.bridge || {};
  const connected = b.state === "connected";

  return (
    <div className="space-y-5" data-testid="whatsapp-settings">
      <div className="grid sm:grid-cols-3 gap-3">
        {MODES.map((m) => (
          <button key={m.key} onClick={() => setMode(m.key)} disabled={busy} data-testid={`wa-mode-${m.key}`}
            className={`text-left rounded-lg border p-3 transition-colors ${st.mode === m.key ? "border-[#0CDBF2] bg-[#0CDBF2]/10" : "border-[#1E293B] bg-[#0B0F17] hover:border-slate-600"}`}>
            <div className="text-sm font-semibold flex items-center gap-2">{m.label}{st.mode === m.key && <span className="w-1.5 h-1.5 rounded-full bg-[#0CDBF2]" />}</div>
            <div className="text-xs text-slate-500 mt-1 leading-relaxed">{m.desc}</div>
          </button>
        ))}
      </div>

      {st.mode === "qr" && (
        <div className="rounded-lg border border-[#1E293B] bg-[#0B0F17] p-4 grid sm:grid-cols-[auto_1fr] gap-5 items-start" data-testid="wa-qr-panel">
          <div className="w-[220px] h-[220px] rounded-md bg-white flex items-center justify-center overflow-hidden">
            {b.state === "qr" && b.qr ? <img src={b.qr} alt="QR Code WhatsApp" className="w-full h-full" data-testid="wa-qr-image" />
              : connected ? <Smartphone size={64} className="text-emerald-500" />
              : <Loader2 size={40} className="text-slate-400 animate-spin" />}
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-slate-400">Status:</span>
              <Badge className={connected ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/40" : "bg-amber-500/20 text-amber-400 border-amber-500/40"} data-testid="wa-state-badge">{STATE_LABEL[b.state] || b.state}</Badge>
            </div>
            {connected && b.me && <div className="text-slate-300">Número conectado: <span className="font-mono text-[#0CDBF2]" data-testid="wa-connected-phone">+{b.me.phone}</span>{b.me.name ? ` · ${b.me.name}` : ""}</div>}
            {!connected && <ol className="text-xs text-slate-400 space-y-1 list-decimal pl-4">
              <li>Abra o WhatsApp no celular → Dispositivos conectados</li>
              <li>Toque em "Conectar dispositivo" e escaneie o QR ao lado</li>
              <li>Mensagens recebidas passam a cair em Conversas e o Consultor IA responde automaticamente</li>
            </ol>}
            {b.lastError && !connected && <div className="text-xs text-slate-500">Último evento: {b.lastError}</div>}
            <div className="flex gap-2 pt-1">
              {!connected && <Button size="sm" onClick={() => act("connect", "Reconectando...")} disabled={busy} className="bg-[#0CDBF2] hover:bg-[#0ABED4] text-[#07090E]" data-testid="wa-connect-btn"><RefreshCw size={14} className="mr-1.5" />{b.state === "qr" ? "Gerar novo QR" : "Conectar"}</Button>}
              {(connected || b.state === "disconnected") && b.me && <Button size="sm" variant="outline" onClick={() => act("logout", "Sessão encerrada")} disabled={busy} className="border-red-500/40 text-red-300 hover:bg-red-500/10" data-testid="wa-logout-btn"><Power size={14} className="mr-1.5" />Desconectar número</Button>}
            </div>
            <div className="text-[11px] text-amber-400/80 flex items-start gap-1.5 pt-1"><QrCode size={12} className="mt-0.5 shrink-0" /> Método não-oficial (WhatsApp Web automatizado). Use um número de testes; para produção, prefira a API oficial.</div>
          </div>
        </div>
      )}

      {st.mode === "api" && (
        <div className="rounded-lg border border-[#1E293B] bg-[#0B0F17] p-4 text-sm space-y-2" data-testid="wa-api-panel">
          <div>Status: <Badge className={st.api_configured ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/40" : "bg-amber-500/20 text-amber-400 border-amber-500/40"}>{st.api_configured ? "Credenciais configuradas" : "Aguardando credenciais"}</Badge></div>
          <div className="text-xs text-slate-500">Webhook: <code className="font-mono text-[#0CDBF2]">/api/webhooks/whatsapp</code>. Configure <code className="font-mono">WHATSAPP_API_TOKEN</code> e <code className="font-mono">WHATSAPP_PHONE_ID</code> no .env para ativar envio pela API oficial da Meta.</div>
        </div>
      )}
    </div>
  );
}
